<template>
  <div>
    <KTCodePreview v-bind:title="'Duyệt danh mục chỉ tiêu'">
      <template v-slot:preview>
        <b-tabs v-model="activeTab" content-class="mt-4" lazy>
          <!-- Tab "Chờ duyệt" (SRS 10.3, FR-301…303, FR-306). Không khóa tab đang mở (bootstrap-vue
               gỡ nội dung tab bị khóa — review P7 #1); chỉ khóa tab "Cảnh báo" khi đang duyệt. -->
          <b-tab>
            <template #title>
              Chờ duyệt
              <span class="label label-sm label-light-primary label-inline ml-2">{{
                pendingTotalRow
              }}</span>
            </template>

            <!-- Bổ sung 01 FR-306 (C31): dải cảnh báo hàng chờ quá hạn — câu SRS nguyên văn, gọn,
                 không nền màu (người dùng chốt phong cách 30/09–01/10/2026). -->
            <p v-if="overdue && overdue.isOverdue" class="overdue-strip mb-3">
              <i class="flaticon2-warning icon-sm text-warning mr-1"></i>
              Có <b>{{ overdue.pendingTotal }}</b> dòng chờ duyệt, dòng lâu nhất đã chờ
              <b>{{ overdue.oldestPendingDays }}</b> ngày. Vui lòng duyệt trong 3 ngày làm việc!
            </p>

            <!-- Thiết kế lại 02/10/2026 (người dùng duyệt): tách hàng LỌC (nhãn trên ô, lưới như các
                 màn khác) khỏi thanh HÀNH ĐỘNG duyệt. -->
            <b-row>
              <b-col cols="12" md="4" lg="3" class="mb-3">
                <label class="font-weight-bold mb-1">Danh mục chỉ tiêu</label>
                <b-form-select
                  v-model="filterCatTargetId"
                  :options="filterOptions"
                  size="sm"
                  :disabled="pendingLoading || isApproving"
                  @change="onFilterChange"
                ></b-form-select>
              </b-col>
            </b-row>

            <!-- Thanh hành động: chỉ khi có UPDATE (FR-602) và đã tích dòng; giữ hiện trong lúc đang
                 duyệt để thấy trạng thái nút. Câu nút giữ nguyên SRS 10.3. -->
            <div
              v-if="canUpdate && (selectedIds.length || isApproving)"
              class="approve-bar d-flex flex-wrap align-items-center mb-3"
            >
              <span class="font-weight-bold mr-2 mb-1">Cách duyệt</span>
              <b-form-select
                v-model="approveTargetId"
                :options="approveOptions"
                size="sm"
                class="approve-select mr-2 mb-1"
                :disabled="isApproving"
              ></b-form-select>
              <SaveButton
                type="button"
                class="mb-1"
                :text="`Duyệt ${selectedIds.length} dòng đã chọn`"
                :is-busy="isApproving"
                :disabled="!selectedIds.length || pendingLoading"
                @click="confirmApprove"
              />
            </div>

            <b-table
              :items="pendingRows"
              :fields="pendingFields"
              :tbody-tr-class="pendingRowClass"
              bordered
              hover
              responsive
              show-empty
              :busy="pendingLoading"
              empty-text="Không có dòng chờ duyệt."
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="5"></vcl-table>
              </template>
              <template v-slot:head(select)>
                <b-form-checkbox
                  :checked="allSelected"
                  :disabled="!pendingRows.length || isApproving"
                  aria-label="Chọn tất cả dòng trên trang"
                  @change="toggleAll"
                ></b-form-checkbox>
              </template>
              <template v-slot:cell(select)="row">
                <b-form-checkbox
                  :checked="selectedIds.includes(row.item.id)"
                  :disabled="isApproving"
                  @change="toggleRow(row.item.id, $event)"
                ></b-form-checkbox>
              </template>
              <template v-slot:cell(productCode)="row">
                <span class="text-break font-weight-bold">{{ row.item.productCode }}</span>
              </template>
              <template v-slot:cell(productName)="row">
                <p class="name-clamp mb-0 text-break" :title="row.item.productName">
                  {{ row.item.productName }}
                </p>
                <!-- Người dùng yêu cầu 01/10/2026: đánh dấu ngay trên dòng trễ (> 7 ngày, FR-306). -->
                <span
                  v-if="row.item.isOverdue"
                  class="label label-light-danger label-inline mt-1"
                  >Đã chờ {{ row.item.waitingDays }} ngày</span
                >
              </template>
              <template v-slot:cell(catTargetCode)="row">
                <span class="label label-light-primary label-inline text-break">{{
                  row.item.catTargetCode
                }}</span>
              </template>
              <template v-slot:cell(effectiveFrom)="row">
                {{ toClientDate(row.item.effectiveFrom) }}
              </template>
              <template v-slot:cell(reason)="row">
                <p class="name-clamp mb-0 text-muted font-size-sm text-break" :title="row.item.reason">
                  {{ row.item.reason }}
                </p>
              </template>
            </b-table>
            <b-row>
              <b-col cols="12" md="4">
                <p class="mt-3 text-dark" style="font-weight: 500">
                  Tổng số: {{ pendingTotalRow }}
                </p>
              </b-col>
              <b-col cols="12" md="8">
                <!-- `:value` + `@change`: số trang chỉ đổi khi tải xong (review P7 #6). -->
                <b-pagination
                  v-if="pendingTotalPage > 1"
                  class="mt-2"
                  :value="pendingPage"
                  :total-rows="pendingTotalRow"
                  :per-page="PAGE_SIZE"
                  :disabled="pendingLoading || isApproving"
                  align="right"
                  size="sm"
                  @change="fetchPending"
                ></b-pagination>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Tab "Cảnh báo" (SRS 10.3, FR-304, FR-305) -->
          <b-tab title="Cảnh báo" :disabled="isApproving">
            <!-- Thiết kế lại 02/10/2026: ô chọn loại cảnh báo cùng kiểu hàng lọc tab Chờ duyệt. -->
            <b-row>
              <b-col cols="12" md="6" lg="4" class="mb-3">
                <label class="font-weight-bold mb-1">Loại cảnh báo</label>
                <b-form-select
                  v-model="alertType"
                  :options="alertOptions"
                  size="sm"
                  :disabled="alertLoading"
                  @change="fetchAlerts(1)"
                ></b-form-select>
              </b-col>
            </b-row>
            <b-table
              :items="alertRows"
              :fields="alertFields"
              bordered
              hover
              responsive
              show-empty
              :busy="alertLoading"
              empty-text="Không có cảnh báo."
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="2"></vcl-table>
              </template>
              <template v-slot:cell(productCode)="row">
                <span class="text-break font-weight-bold">{{ row.item.productCode }}</span>
              </template>
              <template v-slot:cell(productName)="row">
                <p class="name-clamp mb-0 text-break" :title="row.item.productName">
                  {{ row.item.productName }}
                </p>
              </template>
            </b-table>
            <!-- Tổng số dòng + phân trang cùng vị trí với tab Chờ duyệt; câu "{n} dòng." theo SRS 10.3. -->
            <b-row>
              <b-col cols="12" md="4">
                <p v-if="!alertLoading" class="mt-3 text-dark" style="font-weight: 500">
                  {{ alertTotalRow }} dòng
                </p>
              </b-col>
              <b-col cols="12" md="8">
                <b-pagination
                  v-if="alertTotalPage > 1"
                  class="mt-2"
                  :value="alertPage"
                  :total-rows="alertTotalRow"
                  :per-page="PAGE_SIZE"
                  :disabled="alertLoading"
                  align="right"
                  size="sm"
                  @change="fetchAlerts"
                ></b-pagination>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import SaveButton from '@/view/components/common/SaveButton.vue';
import submitLockMixin from '@/view/mixins/submit-lock.mixins';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import localData from '@/utils/saveDataToLocal';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile, makeToastSuccess, getApiErrorMessage } from '@/utils/common';
import { showConfirmNotify } from '@/utils/sweet-alert2';
import {
  DATE_FORMAT,
  YEAR_MONTH_DAY_FORMAT,
  formatSpecificDate,
  getCurrentDate,
} from '@/utils/date';
import { CAT_TARGET_ALERT_TYPE, CAT_TARGET_PAST_PERIOD_CODE } from '@/utils/enum';

// t102962 — Màn 3 Duyệt danh mục chỉ tiêu (SRS 10.3; FR-301…305; Bổ sung 01 FR-306, RULE-27).
const API = 'v2/cat-targets';
const PAGE_SIZE = 50; // SRS 10.3: 50 dòng
const TH = { fontWeight: 600, color: '#181c32' };

export default {
  mixins: [submitLockMixin],
  components: { KTCodePreview, VclTable, SaveButton },
  data() {
    return {
      PAGE_SIZE,
      activeTab: 0,
      selectableTargets: [],
      filterTargets: [],
      // Tab Chờ duyệt
      filterCatTargetId: null,
      approveTargetId: null,
      pendingRows: [],
      pendingPage: 1,
      pendingTotalRow: 0,
      pendingTotalPage: 1,
      pendingLoading: false,
      pendingSeq: 0,
      overdue: null,
      selectedIds: [],
      pendingFields: [
        { key: 'select', label: '', thStyle: { ...TH, width: '4%' }, tdClass: 'align-middle' },
        { key: 'productCode', label: 'Mã', thStyle: { ...TH, width: '15%', minWidth: '110px' } },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          thStyle: { ...TH, width: '37%', minWidth: '200px' },
        },
        {
          key: 'catTargetCode',
          label: 'Danh mục chỉ tiêu đề xuất',
          thStyle: { ...TH, width: '15%', minWidth: '130px' },
          tdClass: 'text-break',
        },
        {
          key: 'effectiveFrom',
          label: 'Hiệu lực từ',
          thStyle: { ...TH, width: '10%', minWidth: '95px' },
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        { key: 'reason', label: 'Căn cứ', thStyle: { ...TH, width: '19%', minWidth: '150px' } },
      ],
      // Tab Cảnh báo
      alertType: CAT_TARGET_ALERT_TYPE.NO_CAT_TARGET,
      alertOptions: [
        {
          value: CAT_TARGET_ALERT_TYPE.NO_CAT_TARGET,
          text: 'Sản phẩm đang bán chưa có danh mục chỉ tiêu',
        },
        {
          value: CAT_TARGET_ALERT_TYPE.COMBO_COMPONENT,
          text: 'Combo có cấu phần chưa có danh mục chỉ tiêu',
        },
      ],
      alertRows: [],
      alertPage: 1,
      alertTotalRow: 0,
      alertTotalPage: 1,
      alertLoading: false,
      alertSeq: 0,
      alertLoaded: false,
      alertFields: [
        { key: 'productCode', label: 'Mã', thStyle: { ...TH, width: '30%', minWidth: '120px' } },
        { key: 'productName', label: 'Tên sản phẩm', thStyle: { ...TH, minWidth: '200px' } },
      ],
    };
  },
  computed: {
    canUpdate() {
      return localData.checkPermission('CAT_TARGET_UPDATE');
    },
    isApproving() {
      return this.isSubmitting('approve');
    },
    // Ô lọc lấy MỌI danh mục chưa xóa (kể cả chưa tới ngày hiệu lực) để lọc được cả dòng chờ duyệt
    // của danh mục đã bị dời ngày; danh mục chưa hiệu lực ghi kèm ngày bắt đầu. Ô "Duyệt sang" vẫn
    // chỉ dùng danh sách chọn hôm nay.
    filterOptions() {
      const today = getCurrentDate(YEAR_MONTH_DAY_FORMAT);
      return [
        { value: null, text: 'Tất cả danh mục chỉ tiêu' },
        ...this.filterTargets.map((item) => ({
          value: item.id,
          text:
            item.effectiveFrom > today
              ? `${item.code} (từ ${this.toClientDate(item.effectiveFrom)})`
              : item.code,
        })),
      ];
    },
    // SRS 10.3: "Giữ danh mục chỉ tiêu đề xuất" / "Duyệt sang {mã}".
    approveOptions() {
      return [
        { value: null, text: 'Giữ danh mục chỉ tiêu đề xuất' },
        ...this.selectableTargets.map((item) => ({
          value: item.id,
          text: `Duyệt sang ${item.code}`,
        })),
      ];
    },
    allSelected() {
      return (
        this.pendingRows.length > 0 &&
        this.pendingRows.every((row) => this.selectedIds.includes(row.id))
      );
    },
  },
  watch: {
    // Tab Cảnh báo chỉ tải khi mở lần đầu.
    activeTab(tab) {
      if (tab === 1 && !this.alertLoaded) {
        this.fetchAlerts(1);
      }
    },
  },
  created() {
    this.fetchSelectable();
    this.fetchFilterTargets();
    this.fetchPending(1);
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Sản phẩm' },
      { title: 'Duyệt danh mục chỉ tiêu' },
    ]);
  },
  methods: {
    toClientDate(date) {
      return formatSpecificDate(date, YEAR_MONTH_DAY_FORMAT, DATE_FORMAT);
    },
    // API #3 — danh sách chọn hôm nay: dùng cho lọc và "Duyệt sang {mã}" (đích phải có hiệu lực
    // ≤ hôm nay, RULE-06e).
    fetchSelectable() {
      ApiService.query(`${API}/selectable`)
        .then(({ data }) => {
          this.selectableTargets = data.data;
        })
        .catch((err) => makeToastFaile(getApiErrorMessage(err)));
    },
    // Mọi danh mục chưa xóa cho ô lọc: đọc hết các trang (API tối đa 100 dòng/trang).
    async fetchFilterTargets() {
      try {
        const targets = [];
        for (let page = 1, totalPage = 1; page <= totalPage; page += 1) {
          const { data } = await ApiService.query(API, { params: { page, limit: 100 } });
          targets.push(...data.data.dataset);
          totalPage = data.data.totalPage;
        }
        this.filterTargets = targets;
      } catch (err) {
        makeToastFaile(getApiErrorMessage(err));
      }
    },
    // FR-301: chỉ nhận kết quả của lần gọi mới nhất; đổi trang/lọc thì bỏ chọn.
    fetchPending(page = 1) {
      const seq = ++this.pendingSeq;
      this.pendingLoading = true;
      this.selectedIds = [];
      const params = { page, limit: PAGE_SIZE };
      if (this.filterCatTargetId) {
        params.catTargetId = this.filterCatTargetId;
      }
      return ApiService.query(`${API}/members/pending`, { params })
        .then(({ data }) => {
          if (seq !== this.pendingSeq) {
            return;
          }
          this.pendingRows = data.data.dataset;
          this.pendingTotalRow = data.data.totalRow;
          this.pendingTotalPage = data.data.totalPage;
          this.pendingPage = data.data.page;
          this.overdue = data.data.overdue;
          // Vừa duyệt hết trang cuối → lùi về trang cuối còn dòng.
          if (!this.pendingRows.length && page > this.pendingTotalPage) {
            this.fetchPending(this.pendingTotalPage);
          }
        })
        .catch((err) => {
          if (seq === this.pendingSeq) {
            makeToastFaile(getApiErrorMessage(err));
          }
        })
        .finally(() => {
          if (seq === this.pendingSeq) {
            this.pendingLoading = false;
          }
        });
    },
    onFilterChange() {
      this.fetchPending(1);
    },
    // Tô nền dòng đang chọn để nhìn rõ phạm vi sẽ duyệt.
    pendingRowClass(item) {
      return item && this.selectedIds.includes(item.id) ? 'row-selected' : '';
    },
    toggleRow(id, checked) {
      this.selectedIds = checked
        ? [...this.selectedIds, id]
        : this.selectedIds.filter((selectedId) => selectedId !== id);
    },
    toggleAll(checked) {
      this.selectedIds = checked ? this.pendingRows.map((row) => row.id) : [];
    },
    /**
     * FR-302 / FR-303. Duyệt sang có dòng thuộc tháng đã qua → API trả `code` RULE-27 → hộp xác
     * nhận cùng nội dung cảnh báo của hộp Thêm sản phẩm (Bổ sung 01 mục 2.5) → gửi lại kèm
     * `confirmPastPeriod`. Gửi lại vẫn đi qua cùng lock (rule 12 §9.1).
     */
    // Người dùng yêu cầu 01/10/2026: hỏi lại trước MỌI lần duyệt để lỡ tích nhầm còn hủy.
    confirmApprove() {
      const count = this.selectedIds.length;
      if (!count || this.isApproving) {
        return;
      }
      const target = this.selectableTargets.find((item) => item.id === this.approveTargetId);
      showConfirmNotify({
        title: 'Xác nhận duyệt',
        text: target
          ? `Duyệt ${count} dòng đã chọn sang ${target.code}?`
          : `Duyệt ${count} dòng đã chọn, giữ danh mục chỉ tiêu đề xuất?`,
        actionName: 'Duyệt',
        confirmButtonColor: '#3699FF',
        icon: 'question',
      }).then((result) => {
        if (result && result.isConfirmed) {
          this.onApprove(false);
        }
      });
    },
    onApprove(confirmPastPeriod) {
      if (!this.selectedIds.length) {
        return;
      }
      let pastPeriodFrom = null;
      return this.withSubmitLock('approve', async () => {
        try {
          const body = { ids: this.selectedIds, confirmPastPeriod };
          if (this.approveTargetId) {
            body.catTargetId = this.approveTargetId;
          }
          const { data } = await ApiService.put(`${API}/members/approve`, body);
          makeToastSuccess(data.message);
          await this.fetchPending(this.pendingPage);
        } catch (err) {
          const response = err && err.response;
          if (response && response.data && response.data.code === CAT_TARGET_PAST_PERIOD_CODE) {
            pastPeriodFrom = (response.data.details || {}).pastPeriodFrom || '';
            return;
          }
          makeToastFaile(getApiErrorMessage(err));
        }
      }).then(() => {
        if (pastPeriodFrom !== null) {
          this.confirmPastPeriod(pastPeriodFrom);
        }
      });
    },
    // `monthYear` lấy từ `details.pastPeriodFrom` của API (review P7 #2).
    confirmPastPeriod(monthYear) {
      showConfirmNotify({
        title: 'Ngày hiệu lực thuộc tháng đã qua',
        text:
          `Ngày hiệu lực thuộc tháng đã qua. Số liệu doanh thu, chỉ tiêu, thưởng của các tháng từ ` +
          `${monthYear} sẽ thay đổi. Nếu muốn đổi cách chia nhóm, nên tạo danh mục chỉ tiêu mới.`,
        actionName: 'Duyệt', // người dùng chốt 01/10/2026
      }).then((result) => {
        if (result && result.isConfirmed) {
          this.onApprove(true);
        }
      });
    },
    // FR-304 / FR-305
    fetchAlerts(page = 1) {
      const seq = ++this.alertSeq;
      this.alertLoading = true;
      const params = { type: this.alertType, page, limit: PAGE_SIZE };
      ApiService.query(`${API}/members/alerts`, { params })
        .then(({ data }) => {
          if (seq !== this.alertSeq) {
            return;
          }
          // Chỉ đánh dấu đã tải khi thành công — lỗi thì mở lại tab sẽ tải lại (review P7 #5).
          this.alertLoaded = true;
          this.alertRows = data.data.dataset;
          this.alertTotalRow = data.data.totalRow;
          this.alertTotalPage = data.data.totalPage;
          this.alertPage = data.data.page;
        })
        .catch((err) => {
          if (seq === this.alertSeq) {
            makeToastFaile(getApiErrorMessage(err));
          }
        })
        .finally(() => {
          if (seq === this.alertSeq) {
            this.alertLoading = false;
          }
        });
    },
  },
};
</script>

<style scoped>
.name-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* Thanh hành động duyệt: nền nhạt để tách khỏi hàng lọc; màn hẹp tự xuống dòng. */
.approve-bar {
  background-color: #f3f6f9;
  border-radius: 0.42rem;
  padding: 0.5rem 0.75rem 0.25rem;
}
/* `.custom-select` của bootstrap ép width 100% — đặt độ rộng cố định, không vượt khung. */
.approve-select {
  width: 260px;
  max-width: 100%;
}
.overdue-strip {
  color: #b58105;
}
::v-deep .row-selected > td {
  background-color: #eef6ff;
}
</style>
